<template>
  <section v-loading="loading" class="view-stack checkin-page">
    <div class="checkin-page-toolbar">
      <span><i class="el-icon-sunny" aria-hidden="true"></i> 平台额外赠送，领取后即可使用</span>
      <el-button size="small" icon="el-icon-refresh" :disabled="loading || checkingIn || historyLoading" @click="refresh">刷新状态</el-button>
    </div>

    <template v-if="ready">
      <div v-if="riskRestricted" class="checkin-restriction is-risk" role="status">
        <div>
          <strong>赠送额度领取已暂停</strong>
          <p>经平台风控审核，当前账号存在异常领取风险，已暂停赠送额度领取权限。已到账额度仍可正常使用，额度信息和历史领取记录仍可查看。</p>
          <p>如对审核结果有异议，请提交工单申请复核。</p>
        </div>
        <el-button size="small" plain @click="$router.push({ name: 'dashboardFeedback' })">申请复核</el-button>
      </div>
      <div class="checkin-stat-grid">
        <article v-for="item in stats" :key="item.label" class="checkin-stat">
          <div class="checkin-stat-label"><span>{{ item.label }}</span><i :class="item.icon" aria-hidden="true"></i></div>
          <strong>{{ item.value }}<small v-if="item.unit">{{ item.unit }}</small></strong>
          <p>{{ item.note }}</p>
          <el-popover v-if="item.showQuotaDetails && quotaReady" placement="bottom-start" :width="280" trigger="click">
            <div class="checkin-quota-details">
              <strong>额度来源</strong>
              <p>系统赠送额度＝账户基础额度＋兑换商品增加额度。</p>
              <dl class="checkin-quota-breakdown">
                <div><dt>账户基础额度</dt><dd>{{ money(quota.initialAmount) }}</dd></div>
                <div><dt>兑换商品增加</dt><dd>{{ formatPrice(quota.totalPurchaseAmount) }}*{{ Number(quota.purchaseMultiplier) }}={{ money(quota.purchaseRewardAmount) }}</dd></div>
              </dl>
              <p>注册赠送 {{ money(100) }}，累计兑换商品 {{ formatPrice(quota.totalPurchaseAmount) }}，每兑换 ￥1，可领取额度增加 {{ money(quota.purchaseMultiplier) }}。</p>
              <el-button type="text" size="small" @click="$router.push({ name: 'dashboardBilling' })">去充值<i class="el-icon-arrow-right" aria-hidden="true"></i></el-button>
            </div>
            <button slot="reference" class="checkin-quota-detail-trigger" type="button">额度来源<i class="el-icon-arrow-right" aria-hidden="true"></i></button>
          </el-popover>
        </article>
      </div>

      <div v-if="!quotaReady" class="checkin-error" role="alert">赠送额度信息暂未加载，请刷新后再领取。<el-button type="text" @click="refresh">重新加载</el-button></div>
      <div v-else-if="allowed && quotaExhausted" class="checkin-quota-exhausted" role="status">
        <div><strong>赠送额度已领完</strong><p>已有余额仍可正常使用。充值购买商品并成功兑换后，就能增加额度继续领取。</p></div>
        <el-button size="small" plain @click="$router.push({ name: 'dashboardBilling' })">去充值</el-button>
      </div>

      <div class="checkin-content-grid">
        <article class="checkin-today-card" :class="{ 'is-complete': availability.checkedToday }">
          <div class="checkin-today-heading">
            <span class="checkin-eyebrow">DAILY CREDITS</span>
            <span class="checkin-status"><i :class="!allowed ? 'el-icon-lock' : availability.checkedToday ? 'el-icon-circle-check' : 'el-icon-time'"></i> {{ !allowed ? '领取已暂停' : availability.checkedToday ? '今日已领取' : quotaExhausted ? '可领取额度已领完' : '今日待领取' }}</span>
          </div>
          <div class="checkin-reward-area">
            <div>
              <p>{{ availability.checkedToday ? '今日已到账额度' : '今日赠送额度' }}</p>
              <strong>{{ todayReward }}</strong>
              <span>
                {{ rewardNote }}
                <el-button v-if="allowed && quotaReady && availability.checkedToday" class="checkin-recharge-link" type="text" size="mini" @click="$router.push({ name: 'dashboardBilling' })">充值送额度</el-button>
              </span>
            </div>
            <div class="checkin-reward-art" aria-hidden="true"><i class="el-icon-date"></i><b>+{{ baseReward }}</b></div>
          </div>
          <el-button class="checkin-submit" type="primary" :loading="checkingIn" :disabled="!allowed || loading || availability.checkedToday || !quotaReady || quotaExhausted" @click="doCheckin">
            <i v-if="!checkingIn" :class="!allowed ? 'el-icon-lock' : availability.checkedToday ? 'el-icon-check' : 'el-icon-present'" aria-hidden="true"></i>
            {{ !allowed ? '领取已暂停' : availability.checkedToday ? '今日已领取' : !quotaReady ? '请先刷新额度信息' : quotaExhausted ? '可领取额度已领完' : '领取今日额度' }}
          </el-button>
          <p v-if="quotaLimitedToday" class="checkin-cap-note">按当前连续领取和任务完成状态，今日标准赠送额度为 {{ money(regularReward) }}；剩余可领取 {{ money(quota.remainingAmount) }}，本次只发放剩余部分，不会超额发放。</p>
          <div class="checkin-progress-heading"><strong>连续领取赠送额度</strong><span>已连续 {{ streak }} 天</span></div>
          <div class="checkin-rules">
            <p>每天可领取一次，连续第 1～10 天分别赠送 $1～$10，之后每天赠送 $10。若中间有一天未领取，下次从第 1 天重新计算。</p>
            <p>系统已经接入风控系统，请勿使用多账号重复领取，违规账号直接关闭领取权限。</p>
          </div>
          <div class="checkin-reward-track">
            <div v-for="day in 10" :key="day" :class="['checkin-reward-step', { checked: day <= streak, current: allowed && !availability.checkedToday && day === Math.min(streak + 1, 10) }]">
              <span>{{ day }}天</span><b>${{ day }}</b><i :class="day <= streak ? 'el-icon-check' : 'el-icon-minus'" aria-hidden="true"></i>
            </div>
          </div>
          <div v-if="taskKeywords" class="checkin-extra-task">
            <div><strong><i class="el-icon-present" aria-hidden="true"></i> 任务加赠 · 最高额外 $5</strong><span :class="{ completed: allowed && !availability.checkedToday && availability.taskResult === 'COMPLETED' }">{{ taskLabel }}</span></div>
            <p>连续领取达到 3 天及以上，在百度搜索“{{ taskKeywords }}”，找到本站地址并点击进入即可完成。完成任务后再领取，额度充足时可额外领取 $5；基础赠送与任务加赠合计不超过剩余可领取额度。</p>
            <p v-if="availability.checkedToday">领取后再完成任务不会补发当日任务加赠，可查看上方到账额度及下方额度领取记录。</p>
          </div>
        </article>

        <article class="checkin-calendar-card">
          <div class="checkin-calendar-heading"><h2>领取日历</h2><span>{{ calendarRecords.length }} 天已领取</span></div>
          <div class="checkin-month-switch">
            <button type="button" aria-label="上个月" :disabled="historyLoading || loading" @click="changeMonth(-1)"><i class="el-icon-arrow-left"></i></button>
            <strong>{{ monthLabel }}</strong>
            <button type="button" aria-label="下个月" :disabled="historyLoading || loading || !canNextMonth" @click="changeMonth(1)"><i class="el-icon-arrow-right"></i></button>
          </div>
          <div v-loading="historyLoading" class="checkin-calendar-grid">
            <span v-for="weekday in weekdays" :key="weekday" class="checkin-weekday">{{ weekday }}</span>
            <div v-for="day in calendarDays" :key="day.key" :class="['checkin-calendar-day', { signed: day.record, today: day.isToday, future: day.future }]" :title="day.record ? day.key + ' 已领取 ' + money(day.record.rewardAmount) : day.key">
              <template v-if="day.number"><span>{{ day.number }}</span><i v-if="day.record" class="el-icon-check" aria-label="已领取"></i><small v-else-if="day.isToday">今天</small></template>
            </div>
          </div>
          <div class="checkin-calendar-legend"><span><i></i> 已领取</span><span><i></i> 今天</span><button type="button" :disabled="historyLoading || loading" @click="backToCurrentMonth">回到本月</button></div>
          <p class="checkin-calendar-note">每日赠送，到账记录清晰可查。</p>
        </article>
      </div>

      <article class="checkin-records-card">
        <div class="checkin-records-heading">
          <div><h2>额度领取记录</h2><p>共 {{ history ? history.total : '--' }} 次 · {{ dateRange && dateRange.length ? '所选期间' : '全部记录' }}赠送到账 <b>{{ history ? money(history.filteredRewardAmount) : '--' }}</b></p></div>
          <div class="checkin-records-filters">
            <el-date-picker v-model="dateRange" type="daterange" size="small" value-format="yyyy-MM-dd" range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" :disabled="historyLoading || loading" @change="filterRecords" />
            <el-button size="small" :disabled="historyLoading || loading" @click="resetFilter">重置</el-button>
          </div>
        </div>
        <div v-if="historyError" class="checkin-error" role="alert"><i class="el-icon-warning-outline"></i> {{ historyError }} <el-button type="text" @click="loadHistory">重试</el-button></div>
        <template v-else>
          <el-table v-loading="historyLoading" :data="records" class="checkin-record-table" empty-text="暂无领取记录，首次领取后会显示在这里">
            <el-table-column prop="checkinDate" label="领取日期" min-width="130" />
            <el-table-column prop="checkinTime" label="领取时间" min-width="185" />
            <el-table-column label="连续领取" min-width="110"><template slot-scope="scope"><span class="checkin-streak-tag">{{ scope.row.consecutiveDays }} 天</span></template></el-table-column>
            <el-table-column label="到账额度" min-width="130" align="right"><template slot-scope="scope"><strong class="checkin-record-amount">+{{ money(scope.row.rewardAmount) }}</strong></template></el-table-column>
          </el-table>
          <div v-loading="historyLoading" class="checkin-record-mobile">
            <div v-for="record in records" :key="record.id" class="checkin-record-mobile-row"><div><strong>{{ record.checkinDate }}</strong><span>{{ record.checkinTime }} · 连续 {{ record.consecutiveDays }} 天</span></div><b>+{{ money(record.rewardAmount) }}</b></div>
            <p v-if="!records.length" class="checkin-empty">暂无领取记录，可领取今日赠送额度。</p>
          </div>
        </template>
        <div v-if="history && history.total > pageSize" class="checkin-pagination"><el-pagination :current-page="pageNo" :page-size="pageSize" :total="history.total" :disabled="historyLoading" layout="prev, pager, next" @current-change="changePage" /></div>
        <p class="checkin-records-note">仅统计有效领取，额度以实际到账记录为准；顶部累计统计不受日期筛选影响。</p>
      </article>
    </template>
    <div v-else-if="!loading" class="checkin-error" role="alert">{{ historyError || '领取状态加载失败，请稍后重试。' }} <el-button type="text" @click="refresh">重新加载</el-button></div>

    <el-dialog
      v-if="quotaReady"
      title="每日赠送额度规则说明"
      :visible.sync="quotaNoticeVisible"
      width="min(520px, calc(100% - 32px))"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :append-to-body="true"
    >
      <div class="checkin-quota-notice">
        <div class="checkin-quota-notice-formula">
          <strong>签到累计可领取上限</strong>
          <span>＝基础额度 {{ money(100) }}＋已兑换商品购买金额（元）× {{ Number(quota.purchaseMultiplier) }}</span>
        </div>
        <p>例如：购买并成功兑换 ￥20 的商品，累计可领取上限增加 <strong>{{ money(20 * Number(quota.purchaseMultiplier)) }}</strong>，合计为 <strong>{{ money(100 + 20 * Number(quota.purchaseMultiplier)) }}</strong>。</p>
        <p>这是累计可领取上限，<strong>不是每日赠送额度，也不是当前可用余额</strong>。</p>
        <p v-if="Number(quota.initialAmount) > 100" class="checkin-quota-notice-note">本账号基础额度为 {{ money(quota.initialAmount) }}，包含老用户补充额度，实际累计可领取上限以「额度来源」为准。</p>
      </div>
      <span slot="footer">
        <el-button @click="disableQuotaNotice">不再提醒</el-button>
        <el-button type="primary" @click="closeQuotaNotice">我已了解</el-button>
      </span>
    </el-dialog>
  </section>
</template>

<script>
import { fetchCheckinAvailability, fetchCheckinRecords, submitCheckin } from '../../api/checkin';
import { fetchProfile } from '../../api/profile';

const QUOTA_NOTICE_STORAGE_PREFIX = 'codex-api-checkin-quota-notice-dismissed-v1:';
const suppressedQuotaNotices = new Set();

export default {
  name: 'CheckinView',
  data() {
    return {
      ready: false,
      allowed: false,
      loading: false,
      checkingIn: false,
      disposed: false,
      leaving: false,
      historyLoading: false,
      historyReloadRequested: false,
      historyError: '',
      quotaNoticeVisible: false,
      quotaNoticeHandled: false,
      quotaNoticeStorageKey: '',
      availability: { visible: false, allowed: false, restrictionReason: null, checkedToday: false, consecutiveDays: 0, taskResult: 'LESS_THAN_3' },
      taskKeywords: '',
      profileLoaded: false,
      history: null,
      latestRewardAmount: null,
      month: '',
      dateRange: [],
      pageNo: 1,
      pageSize: 10,
      weekdays: ['一', '二', '三', '四', '五', '六', '日']
    };
  },
  computed: {
    riskRestricted() { return !this.allowed && this.availability.restrictionReason === 'RISK_CONTROL'; },
    streak() { return Math.max(0, Number(this.availability.consecutiveDays) || 0); },
    baseReward() { return Math.min(10, this.streak + (this.availability.checkedToday ? 0 : 1)); },
    quota() { return this.availability.quota || null; },
    quotaReady() {
      return Boolean(this.quota && ['initialAmount', 'totalPurchaseAmount', 'purchaseMultiplier', 'purchaseRewardAmount', 'totalClaimableAmount', 'totalClaimedAmount', 'remainingAmount']
        .every(field => this.quota[field] !== null && this.quota[field] !== undefined && this.quota[field] !== '' && Number.isFinite(Number(this.quota[field]))));
    },
    quotaExhausted() { return this.quotaReady && Number(this.quota.remainingAmount) <= 0; },
    regularReward() { return this.baseReward + (this.availability.taskResult === 'COMPLETED' ? 5 : 0); },
    quotaLimitedToday() { return this.allowed && !this.availability.checkedToday && this.quotaReady && !this.quotaExhausted && Number(this.quota.remainingAmount) < this.regularReward; },
    taskExtraAvailable() { return this.quotaReady ? Math.min(5, Math.max(0, Number(this.quota.remainingAmount) - this.baseReward)) : 0; },
    rewardNote() {
      if (!this.allowed) return '赠送额度领取已暂停，历史到账额度不受影响';
      if (!this.quotaReady) return '请先刷新赠送额度信息';
      if (this.availability.checkedToday) {
        const remainingAmount = Math.max(0, Number(this.quota.totalClaimableAmount) - Number(this.quota.totalClaimedAmount));
        return '今日赠送已到账；您还可领取 ' + this.money(remainingAmount);
      }
      if (this.quotaExhausted) return '可领取额度已领完，已有到账余额不受影响';
      return '领取后发放至余额分组，不会自动到账';
    },
    todayReward() {
      if (this.availability.checkedToday) {
        if (this.latestRewardAmount !== null) return this.money(this.latestRewardAmount);
        return this.history ? this.money(this.history.todayRewardAmount) : '--';
      }
      return this.allowed && this.quotaReady ? this.money(Math.min(this.regularReward, Number(this.quota.remainingAmount))) : '--';
    },
    taskLabel() {
      if (!this.allowed) return '领取已暂停';
      if (this.availability.checkedToday) return '今日已领取，赠送额度以实际到账为准';
      return { COMPLETED: '已完成', LESS_THAN_3: '连续领取满 3 天解锁', NO_BAIDU: '待完成' }[this.availability.taskResult] || '待完成';
    },
    stats() {
      return [
        { label: '系统赠送额度', value: this.quotaReady ? this.money(this.quota.totalClaimableAmount) : '--', icon: 'el-icon-present', note: '系统赠送，每日领取后到账', showQuotaDetails: true },
        { label: '已领取额度', value: this.history ? this.money(this.history.totalRewardAmount) : '--', icon: 'el-icon-wallet', note: '历史赠送实际到账额度' },
        { label: '累计领取次数', value: this.history ? Number(this.history.totalCount).toLocaleString('zh-CN') : '--', unit: '次', icon: 'el-icon-finished', note: '每个成功领取日计一次' },
        { label: '连续领取天数', value: this.streak, unit: '天', icon: 'el-icon-sunny', note: this.streak >= 10 ? '每日基础赠送额度为 $10' : '连续领取，基础赠送额度逐日递增' }
      ];
    },
    records() { return this.history ? this.history.records || [] : []; },
    calendarRecords() { return this.history ? this.history.calendarRecords || [] : []; },
    monthLabel() {
      const parts = this.month.split('-');
      return this.month ? parts[0] + ' 年 ' + Number(parts[1]) + ' 月' : '--';
    },
    canNextMonth() { return Boolean(this.history && this.month < this.history.businessDate.slice(0, 7)); },
    calendarDays() {
      if (!this.month || !this.history) return [];
      const [year, month] = this.month.split('-').map(Number);
      const padding = (new Date(year, month - 1, 1).getDay() + 6) % 7;
      const length = new Date(year, month, 0).getDate();
      const recordByDate = this.calendarRecords.reduce((result, record) => {
        result[record.checkinDate] = record;
        return result;
      }, {});
      return Array.from({ length: Math.ceil((padding + length) / 7) * 7 }, (_, index) => {
        const number = index - padding + 1;
        if (number < 1 || number > length) return { key: 'blank-' + index };
        const key = this.month + '-' + String(number).padStart(2, '0');
        return { key, number, record: recordByDate[key], isToday: key === this.history.businessDate, future: key > this.history.businessDate };
      });
    }
  },
  created() {
    this.$root.$on('checkin-availability-updated', this.applyAvailability);
    this.refresh();
  },
  beforeDestroy() {
    this.disposed = true;
    this.$root.$off('checkin-availability-updated', this.applyAvailability);
  },
  methods: {
    showQuotaNotice() {
      if (this.disposed || this.leaving || !this.ready || !this.quotaReady || this.quotaNoticeHandled) return;
      try {
        const account = window.localStorage.getItem('codex-api-login-user');
        if (!account) return;
        this.quotaNoticeStorageKey = QUOTA_NOTICE_STORAGE_PREFIX + account;
        if (window.localStorage.getItem(this.quotaNoticeStorageKey) === '1') {
          suppressedQuotaNotices.add(this.quotaNoticeStorageKey);
        }
      } catch (error) {
        this.quotaNoticeStorageKey = this.quotaNoticeStorageKey || QUOTA_NOTICE_STORAGE_PREFIX + 'session';
      }
      this.quotaNoticeHandled = true;
      this.quotaNoticeVisible = !suppressedQuotaNotices.has(this.quotaNoticeStorageKey);
    },
    closeQuotaNotice() {
      this.quotaNoticeVisible = false;
    },
    disableQuotaNotice() {
      this.closeQuotaNotice();
      suppressedQuotaNotices.add(this.quotaNoticeStorageKey);
      try {
        window.localStorage.setItem(this.quotaNoticeStorageKey, '1');
      } catch (error) {
        return;
      }
    },
    money(value) {
      return '$' + Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
    },
    formatPrice(value) {
      return '￥' + Number(value || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
    },
    applyAvailability(value) {
      if (this.disposed) return;
      const availability = { ...this.availability, ...value };
      availability.visible = typeof value.visible === 'boolean' ? value.visible : value.allowed === true;
      const changed = this.availability.checkedToday !== Boolean(availability.checkedToday)
        || this.availability.consecutiveDays !== availability.consecutiveDays
        || this.allowed !== (availability.allowed === true);
      this.availability = availability;
      this.allowed = availability.visible && availability.allowed === true;
      if (!availability.visible) {
        this.leaveUnavailable();
      } else if (changed && this.ready && !this.loading && !this.checkingIn) {
        this.latestRewardAmount = null;
        this.loadHistory();
      }
    },
    leaveUnavailable() {
      if (this.leaving || this.disposed) return;
      this.leaving = true;
      this.ready = false;
      this.$message.warning('每日赠额度功能暂不可用');
      if (this.$route.name === 'dashboardCheckin') this.$router.replace({ name: 'dashboardOverview' });
    },
    async loadAvailability() {
      const { data } = await fetchCheckinAvailability();
      if (!data || data.code !== 0 || !data.data) throw new Error('领取状态加载失败，请稍后重试');
      if (!this.disposed) this.$root.$emit('checkin-availability-updated', data.data);
      return data.data;
    },
    async loadTaskKeywords() {
      if (this.profileLoaded) return;
      try {
        const { data } = await fetchProfile();
        if (data && data.code === 0 && data.data) {
          this.taskKeywords = String(data.data.websiteSeoKeywords || '').trim();
          this.profileLoaded = true;
        }
      } catch (error) {
        this.taskKeywords = '';
      }
    },
    async refresh() {
      if (this.loading || this.checkingIn || this.disposed) return;
      this.loading = true;
      this.historyError = '';
      try {
        await this.loadAvailability();
        if (this.disposed || this.leaving) return;
        this.ready = true;
        this.latestRewardAmount = null;
        await Promise.all([this.loadHistory(), this.loadTaskKeywords()]);
        this.showQuotaNotice();
      } catch (error) {
        this.historyError = error.message || '领取状态加载失败，请稍后重试';
      } finally {
        this.loading = false;
      }
    },
    async loadHistory() {
      if (this.disposed || this.leaving) return;
      if (this.historyLoading) {
        this.historyReloadRequested = true;
        return;
      }
      this.historyLoading = true;
      this.historyError = '';
      try {
        const params = { pageNo: this.pageNo, pageSize: this.pageSize, month: this.month || undefined };
        if (this.dateRange && this.dateRange.length === 2) [params.startDate, params.endDate] = this.dateRange;
        const { data } = await fetchCheckinRecords(params);
        if (this.disposed) return;
        if (data && data.code === 403) {
          this.$root.$emit('checkin-availability-updated', { visible: false, allowed: false, restrictionReason: null });
          return;
        }
        if (!data || data.code !== 0 || !data.data) throw new Error((data && data.message) || '额度领取记录加载失败，请稍后重试');
        this.history = data.data;
        if (!this.month) this.month = data.data.businessDate.slice(0, 7);
      } catch (error) {
        this.historyError = error.message || '额度领取记录加载失败，请稍后重试';
      } finally {
        this.historyLoading = false;
        if (this.historyReloadRequested) {
          this.historyReloadRequested = false;
          this.loadHistory();
        }
      }
    },
    async doCheckin() {
      if (this.checkingIn || this.loading || !this.allowed || this.availability.checkedToday || !this.quotaReady || this.quotaExhausted) return;
      this.checkingIn = true;
      let refreshAfterError = false;
      try {
        const availability = await this.loadAvailability();
        if (!availability.allowed || this.disposed) return;
        if (availability.checkedToday) {
          this.$message.info('今日已领取');
          await this.loadHistory();
          return;
        }
        if (!this.quotaReady) throw new Error('赠送额度信息加载失败，请刷新后重试');
        if (this.quotaExhausted) {
          this.$message.warning('剩余可领额度已领完，已有到账余额不受影响');
          return;
        }
        if (this.taskKeywords && this.streak >= 2 && availability.taskResult === 'NO_BAIDU' && this.taskExtraAvailable > 0) {
          try {
            await this.$confirm('今日任务尚未完成，按当前剩余额度，完成后再领取最多可额外领取 ' + this.money(this.taskExtraAvailable) + '。现在领取将无法补领今日任务加赠，确定继续吗？', '任务尚未完成', { confirmButtonText: '确定领取', cancelButtonText: '取消', type: 'warning' });
          } catch (error) {
            return;
          }
        }
        if (this.disposed || !this.allowed) return;
        const { data } = await submitCheckin();
        if (!data || data.code !== 0) throw new Error((data && data.message) || '领取失败，请稍后重试');
        const result = data.data;
        this.latestRewardAmount = result.rewardAmount;
        this.$root.$emit('checkin-availability-updated', { visible: true, allowed: true, restrictionReason: null, checkedToday: true, consecutiveDays: result.consecutiveDays, taskResult: result.taskResult, quota: result.quota });
        this.$root.$emit('checkin-completed');
        this.$message.success('领取成功，余额分组已增加 ' + this.money(result.rewardAmount) + ' 额度' + (result.quotaLimited ? '；因剩余可领取额度不足，已按剩余部分发放' : ''));
        this.pageNo = 1;
        await this.loadHistory();
      } catch (error) {
        if (error.message === 'CHECKIN_UNAVAILABLE') {
          this.allowed = false;
          refreshAfterError = true;
        } else {
          const messages = { CHECKIN_ALREADY_COMPLETED: '今日已领取', CHECKIN_QUOTA_EXHAUSTED: '剩余可领额度已领完，已有到账余额不受影响' };
          this.$message.error(messages[error.message] || error.message || '领取失败，请稍后重试');
          refreshAfterError = true;
        }
      } finally {
        this.checkingIn = false;
        if (refreshAfterError) this.refresh();
      }
    },
    filterRecords() { this.pageNo = 1; this.loadHistory(); },
    resetFilter() { this.dateRange = []; this.filterRecords(); },
    changePage(page) { this.pageNo = page; this.loadHistory(); },
    changeMonth(offset) {
      if (!this.month || this.historyLoading) return;
      const [year, month] = this.month.split('-').map(Number);
      const date = new Date(year, month - 1 + offset, 1);
      this.month = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0');
      this.loadHistory();
    },
    backToCurrentMonth() {
      if (!this.history || this.historyLoading) return;
      this.month = this.history.businessDate.slice(0, 7);
      this.loadHistory();
    }
  }
};
</script>

<style scoped>
.checkin-quota-notice { color: #536b60; font-size: 14px; line-height: 1.9; overflow-wrap: anywhere; }
.checkin-quota-notice p { margin: 0 0 14px; }
.checkin-quota-notice strong { color: #176f5e; }
.checkin-quota-notice-formula { display: grid; gap: 6px; margin-bottom: 14px; padding: 16px; border: 1px solid #dce8e2; border-radius: 10px; background: #f5faf7; }
.checkin-quota-notice .checkin-quota-notice-note { margin-bottom: 0; color: #8c9c93; font-size: 12px; }
.checkin-page { min-height: 240px; color: #193b35; }
.checkin-page-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.checkin-page-toolbar > span { color: #638078; font-size: 13px; }
.checkin-page-toolbar > span i { margin-right: 5px; color: #b9822a; }
.checkin-quota-detail-trigger { display: inline-flex; align-items: center; gap: 4px; padding: 4px 0; border: 0; background: transparent; color: #5b7d6b; cursor: pointer; font-size: 12px; }
.checkin-quota-detail-trigger:hover { color: #20856c; }
.checkin-quota-details { color: #536b60; font-size: 12px; }
.checkin-quota-details > strong { color: #193b35; font-size: 14px; }
.checkin-quota-breakdown { display: grid; gap: 10px; margin: 14px 0 0; }
.checkin-quota-breakdown > div { display: flex; justify-content: space-between; gap: 16px; }
.checkin-quota-breakdown dt { color: #6b817c; }
.checkin-quota-breakdown dd { min-width: 0; margin: 0; text-align: right; font-weight: 600; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
.checkin-quota-details p { margin: 10px 0 0; line-height: 1.8; overflow-wrap: anywhere; }
.checkin-quota-details .checkin-quota-detail-note { padding-top: 10px; border-top: 1px solid #e8eeea; color: #8c9c93; }
.checkin-restriction { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px; border: 1px solid #dce8e2; border-radius: 12px; background: #f5faf7; color: #536b60; font-size: 13px; }
.checkin-restriction.is-risk { border-color: #ecdcb9; background: #fff9ee; color: #8c692f; }
.checkin-restriction p { margin: 6px 0 0; font-size: 12px; line-height: 1.8; }
.checkin-restriction .el-button { flex-shrink: 0; }
.checkin-quota-exhausted { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 14px; border: 1px solid #ecdcb9; border-radius: 10px; background: #fff9ee; color: #8c692f; font-size: 13px; }
.checkin-quota-exhausted p { margin: 6px 0 0; font-size: 12px; line-height: 1.8; }
.checkin-cap-note { margin: 10px 0; color: #967139; font-size: 12px; line-height: 1.8; }
.checkin-stat-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.checkin-stat, .checkin-today-card, .checkin-calendar-card, .checkin-records-card {
  min-width: 0;
  border: 1px solid #deebe6;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 5px 22px rgba(35, 83, 65, .035);
}
.checkin-stat { padding: 20px 22px; }
.checkin-stat .checkin-quota-detail-trigger { margin-top: 4px; }
.checkin-stat-label { display: flex; justify-content: space-between; align-items: center; gap: 8px; color: #6b817c; font-size: 13px; }
.checkin-stat-label i { display: grid; place-items: center; width: 30px; height: 30px; border-radius: 9px; background: #edf8f3; color: #20856c; font-size: 17px; }
.checkin-stat:first-child .checkin-stat-label i { background: #fff6e6; color: #b58733; }
.checkin-stat > strong { display: block; margin-top: 10px; font-size: clamp(23px, 2.4vw, 32px); font-weight: 750; letter-spacing: -.7px; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
.checkin-stat > strong small { margin-left: 6px; color: #789087; font-size: 12px; font-weight: 400; }
.checkin-stat > p { margin: 9px 0 0; color: #8b9b95; font-size: 12px; line-height: 1.5; }
.checkin-content-grid { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(300px, 1fr); gap: 20px; align-items: stretch; }
.checkin-today-card { position: relative; overflow: hidden; padding: 26px; background: linear-gradient(130deg, #f0fbf5 0%, #fff 65%); }
.checkin-today-heading { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.checkin-eyebrow { color: #7b9a8c; font-size: 11px; font-weight: 700; letter-spacing: 2px; }
.checkin-status { display: inline-flex; align-items: center; gap: 5px; padding: 6px 10px; border-radius: 20px; color: #9b722e; background: #fff4dc; font-size: 12px; }
.is-complete .checkin-status { color: #20846a; background: #e0f4e9; }
.checkin-reward-area { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin: 24px 0; }
.checkin-reward-area p { margin: 0 0 6px; color: #6d867c; font-size: 13px; }
.checkin-reward-area strong { display: block; color: #17664e; font-size: clamp(34px, 4vw, 48px); line-height: 1.25; letter-spacing: -1.5px; overflow-wrap: anywhere; }
.checkin-reward-area > div > span { display: block; margin-top: 9px; color: #8b9a92; font-size: 12px; line-height: 1.6; }
.checkin-recharge-link.el-button { margin-left: 8px; padding: 0; font-size: 12px; }
.checkin-reward-art { position: relative; display: grid; place-items: center; flex: 0 0 92px; height: 92px; margin: 0 10px; border: 7px solid #edf7ee; border-radius: 28px; background: #dceee0; color: #6b9f79; transform: rotate(-8deg); }
.checkin-reward-art > i { font-size: 43px; }
.checkin-reward-art > b { position: absolute; right: -14px; bottom: -4px; padding: 5px 10px; border: 3px solid #fff; border-radius: 18px; color: #9f7730; background: #ffe5ac; font-size: 15px; transform: rotate(8deg); }
.checkin-submit.el-button { display: block; width: 100%; margin: 0 0 24px; padding: 15px 12px; border-radius: 10px; font-size: 15px; font-weight: 600; }
.checkin-submit.el-button--primary { background: #188365; border-color: #188365; box-shadow: 0 5px 12px rgba(24, 131, 101, .12); }
.checkin-submit.el-button--primary:not(.is-disabled):hover { background: #116d53; border-color: #116d53; }
.checkin-submit.el-button.is-disabled { background: #e6f3eb; border-color: #d4e9dc; color: #65957a; box-shadow: none; }
.checkin-progress-heading { display: flex; justify-content: space-between; gap: 10px; font-size: 12px; }
.checkin-progress-heading > strong { font-size: 13px; }
.checkin-progress-heading > span { color: #728c80; }
.checkin-reward-track { display: grid; grid-template-columns: repeat(10, minmax(0, 1fr)); gap: 5px; margin: 12px 0 20px; }
.checkin-reward-step { display: flex; flex-direction: column; align-items: center; gap: 7px; padding: 10px 0 8px; border: 1px solid #edf0ed; border-radius: 8px; color: #b0bdb6; background: #fafcfb; }
.checkin-reward-step > span { font-size: 10px; }
.checkin-reward-step > b { color: #8a9c92; font-size: 13px; }
.checkin-reward-step > i { font-size: 11px; }
.checkin-reward-step.checked { color: #4b9c77; background: #ebf6ee; border-color: #e0eee4; }
.checkin-reward-step.checked > b { color: #3a8a65; }
.checkin-reward-step.current { background: #fff8e9; border-color: #e7c789; color: #b89043; }
.checkin-reward-step.current > b { color: #a57b2d; }
.checkin-extra-task { padding: 13px 15px; border: 1px solid #e4eee8; border-radius: 10px; background: rgba(255, 255, 255, .8); }
.checkin-extra-task > div { display: flex; justify-content: space-between; gap: 8px; align-items: center; flex-wrap: wrap; }
.checkin-extra-task strong { font-size: 12px; font-weight: 600; }
.checkin-extra-task strong i { color: #b3883d; }
.checkin-extra-task > div > span { color: #a28c62; font-size: 11px; }
.checkin-extra-task > div > span.completed { color: #188365; }
.checkin-extra-task p { margin: 8px 0 0; color: #84968c; font-size: 12px; line-height: 1.8; }
.checkin-rules { margin-top: 8px; color: #87988e; font-size: 12px; line-height: 1.8; }
.checkin-rules p { margin: 0; }
.checkin-rules p + p { margin-top: 8px; }
.checkin-calendar-card { display: flex; flex-direction: column; padding: 24px; }
.checkin-calendar-heading, .checkin-month-switch { display: flex; justify-content: space-between; align-items: center; gap: 10px; }
.checkin-page h2 { margin: 0; color: #25463a; font-size: 16px; font-weight: 650; }
.checkin-calendar-heading > span { color: #83968c; font-size: 12px; }
.checkin-month-switch { margin: 23px 0 16px; }
.checkin-month-switch > strong { color: #536e5f; font-size: 14px; font-weight: 600; }
.checkin-month-switch button { display: grid; place-items: center; width: 28px; height: 28px; padding: 0; border: 1px solid #e8eee9; border-radius: 7px; background: #fff; color: #6c8978; cursor: pointer; }
.checkin-month-switch button:disabled { opacity: .35; cursor: not-allowed; }
.checkin-calendar-grid { display: grid; flex: 1; grid-template-columns: repeat(7, minmax(0, 1fr)); grid-template-rows: auto; grid-auto-rows: 1fr; gap: 6px; }
.checkin-weekday { padding: 0 0 8px; color: #a1b0a5; text-align: center; font-size: 11px; }
.checkin-calendar-day { display: flex; min-height: 45px; flex-direction: column; align-items: center; justify-content: center; gap: 3px; border: 1px solid transparent; border-radius: 9px; color: #7e9184; font-size: 13px; }
.checkin-calendar-day.signed { color: #348564; background: #edf7ef; }
.checkin-calendar-day.today { border-color: #90b99f; color: #2a7a57; font-weight: 600; }
.checkin-calendar-day.future { color: #c3cdc5; }
.checkin-calendar-day > i { font-size: 10px; }
.checkin-calendar-day > small { font-size: 9px; font-weight: 400; }
.checkin-calendar-legend { display: flex; gap: 16px; align-items: center; margin-top: 20px; padding-top: 18px; border-top: 1px solid #f0f3f0; font-size: 11px; color: #8a9e8f; }
.checkin-calendar-legend > span { display: inline-flex; align-items: center; gap: 5px; }
.checkin-calendar-legend i { width: 8px; height: 8px; border-radius: 2px; background: #c7e5d2; }
.checkin-calendar-legend > span:nth-child(2) i { border: 1px solid #90b99f; background: #fff; }
.checkin-calendar-legend button { margin-left: auto; padding: 0; border: 0; background: none; color: #599174; font-size: 11px; cursor: pointer; }
.checkin-calendar-legend button:disabled { opacity: .4; cursor: not-allowed; }
.checkin-calendar-note { margin: 22px 0 0; color: #acb9ae; font-size: 11px; text-align: center; }
.checkin-records-card { padding: 24px; }
.checkin-records-heading { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 18px; margin-bottom: 20px; }
.checkin-records-heading p { margin: 8px 0 0; color: #8b9d90; font-size: 12px; }
.checkin-records-heading p b { margin-left: 3px; color: #649174; font-weight: 500; }
.checkin-records-filters { display: flex; gap: 10px; align-items: center; max-width: 100%; }
.checkin-records-filters .el-date-editor { width: 400px; max-width: 100%; }
.checkin-streak-tag { display: inline-block; padding: 3px 8px; border-radius: 5px; background: #f0f6f1; color: #6e8f7a; font-size: 12px; }
.checkin-record-amount { color: #258363; font-weight: 600; font-variant-numeric: tabular-nums; }
.checkin-record-mobile { display: none; }
.checkin-pagination { display: flex; justify-content: flex-end; margin-top: 18px; }
.checkin-records-note { margin: 20px 0 0; color: #9eada2; font-size: 11px; line-height: 1.8; }
.checkin-error { padding: 16px; color: #9b793c; border: 1px solid #f0e4c8; border-radius: 10px; background: #fffaf0; font-size: 13px; }
.checkin-error .el-button { margin-left: 8px; }
.checkin-page button:focus-visible, .checkin-page summary:focus-visible { outline: 2px solid #258363; outline-offset: 3px; }
@media (max-width: 1100px) {
  .checkin-stat-grid { gap: 12px; }
  .checkin-stat { padding: 18px 16px; }
  .checkin-content-grid { grid-template-columns: minmax(0, 1fr) minmax(280px, .85fr); gap: 16px; }
  .checkin-today-card, .checkin-calendar-card { padding: 20px; }
  .checkin-reward-track { grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 7px; }
  .checkin-reward-step { padding: 8px 0; gap: 5px; }
}
@media (max-width: 820px) {
  .checkin-quota-exhausted, .checkin-restriction { align-items: flex-start; flex-direction: column; }
  .checkin-stat-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .checkin-content-grid { grid-template-columns: minmax(0, 1fr); }
  .checkin-reward-track { grid-template-columns: repeat(10, minmax(0, 1fr)); }
  .checkin-calendar-day { min-height: 48px; }
}
@media (max-width: 640px) {
  .checkin-stat { padding: 16px; }
  .checkin-stat > strong { font-size: 25px; }
  .checkin-stat-label { font-size: 12px; }
  .checkin-stat-label i { width: 25px; height: 25px; font-size: 14px; }
  .checkin-stat > p { font-size: 10px; }
  .checkin-today-card, .checkin-calendar-card, .checkin-records-card { padding: 18px; border-radius: 12px; }
  .checkin-reward-art { flex-basis: 72px; height: 72px; margin-right: 7px; border-radius: 22px; }
  .checkin-reward-art > i { font-size: 34px; }
  .checkin-reward-art > b { font-size: 12px; }
  .checkin-reward-track { grid-template-columns: repeat(5, minmax(0, 1fr)); }
  .checkin-records-filters { width: 100%; }
  .checkin-records-filters .el-date-editor { flex: 1; min-width: 0; }
  .checkin-records-filters > .el-button { flex: 0 0 auto; }
  .checkin-record-table { display: none; }
  .checkin-record-mobile { display: block; }
  .checkin-record-mobile-row { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding: 16px 0; border-top: 1px solid #eef3ed; }
  .checkin-record-mobile-row > div { min-width: 0; }
  .checkin-record-mobile-row strong { font-size: 13px; font-weight: 500; }
  .checkin-record-mobile-row span { display: block; margin-top: 7px; color: #96a497; font-size: 10px; line-height: 1.6; }
  .checkin-record-mobile-row > b { flex-shrink: 0; color: #258363; font-size: 14px; font-weight: 600; }
  .checkin-empty { padding: 24px 0; color: #9eada2; text-align: center; font-size: 12px; line-height: 1.8; }
}
</style>
